import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Button,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Avatar,
  LinearProgress,
  CardHeader,
  Divider
} from "@mui/material";
import { 
  Refresh, 
  TrendingUp, 
  TrendingDown, 
  Inventory, 
  LocalShipping,
  Business,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Timeline,
  Analytics,
  Dashboard as DashboardIcon
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { getDashboardStats, getLinenItemStats, getLowStockItems } from "../services/api";

// Enhanced StatsCard component with beautiful styling
const StatsCard = ({ title, value, subtitle, loading = false, trend, icon, color = "primary", ...props }) => (
  <Card 
    {...props} 
    sx={{ 
      height: '100%',
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 48px rgba(0,0,0,0.15)'
      },
      ...props.sx 
    }}
  >
    <CardContent sx={{ p: 4, position: 'relative' }}>
      {/* Icon Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          opacity: 0.1,
          '& .MuiSvgIcon-root': {
            fontSize: '4rem',
            color: `${color}.main`
          }
        }}
      >
        {icon}
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          {title}
          {trend !== undefined && (
            trend > 0 ? 
              <TrendingUp color="success" sx={{ fontSize: '1.5rem' }} /> :
            trend < 0 ? 
              <TrendingDown color="error" sx={{ fontSize: '1.5rem' }} /> :
              <Timeline color="disabled" sx={{ fontSize: '1.5rem' }} />
          )}
        </Typography>
        
        {/* Front Icon */}
        <Box sx={{ color: `${color}.main` }}>
          {React.cloneElement(icon, { sx: { fontSize: '2rem' } })}
        </Box>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={40} thickness={4} />
        </Box>
      ) : (
        <>
          <Typography 
            variant="h1" 
            component="div" 
            fontWeight="bold" 
            color={`${color}.main`}
            sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              mb: 1,
              lineHeight: 1.1
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                mt: 2,
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              {subtitle}
            </Typography>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

// Enhanced Low Stock Alert Card
const LowStockAlert = ({ items, loading }) => (
  <Card 
    sx={{ 
      bgcolor: 'warning.light', 
      color: 'warning.contrastText',
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(255,152,0,0.2)',
      border: '2px solid',
      borderColor: 'warning.main'
    }}
  >
    <CardContent sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Warning sx={{ fontSize: '2.5rem' }} />
        <Box>
          <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
            ⚠️ Low Stock Alert
          </Typography>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', opacity: 0.9 }}>
            Immediate attention required
          </Typography>
        </Box>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={30} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Checking stock levels...
          </Typography>
        </Box>
      ) : items && items.length > 0 ? (
        <Box>
          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 2 }}>
            🚨 {items.length} item(s) below minimum stock level
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {items.slice(0, 6).map((item, index) => (
              <Chip
                key={item._id || index}
                label={`${item.name} (${item.quantity}/${item.min_stock})`}
                size="large"
                color="warning"
                variant="filled"
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  py: 2,
                  mb: 1
                }}
              />
            ))}
            {items.length > 6 && (
              <Chip
                label={`+${items.length - 6} more`}
                size="large"
                variant="outlined"
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  py: 2,
                  mb: 1
                }}
              />
            )}
          </Stack>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CheckCircle sx={{ fontSize: '2rem' }} />
          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
            ✅ All items are well stocked
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// Enhanced Error Fallback Component
const ErrorFallback = ({ error, onRetry }) => (
  <Box sx={{ p: 6, textAlign: 'center' }}>
    <Card 
      sx={{ 
        maxWidth: 500, 
        mx: 'auto',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <ErrorIcon sx={{ fontSize: '4rem', color: 'error.main', mb: 3 }} />
        <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700, mb: 2 }}>
          Failed to Load Dashboard
        </Typography>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            fontSize: '1.1rem',
            borderRadius: 2
          }}
        >
          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
            {error || "An unexpected error occurred"}
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          onClick={onRetry} 
          startIcon={<Refresh sx={{ fontSize: '1.5rem' }} />}
          sx={{ 
            fontSize: '1.2rem',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 3
          }}
        >
          Try Again
        </Button>
      </CardContent>
    </Card>
  </Box>
);

// Enhanced Loading Component
const DashboardLoading = () => (
  <Box sx={{ 
    display: "flex", 
    justifyContent: "center", 
    alignItems: "center", 
    minHeight: 500,
    flexDirection: 'column',
    gap: 3
  }}>
    <CircularProgress size={80} thickness={4} />
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h3" sx={{ fontSize: '2rem', fontWeight: 700, mb: 1 }}>
        Loading Dashboard...
      </Typography>
      <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
        Preparing your analytics and statistics
      </Typography>
    </Box>
  </Box>
);

// Enhanced Empty State Component
const EmptyState = ({ onRetry }) => (
  <Box sx={{ p: 6, textAlign: 'center' }}>
    <Card 
      sx={{ 
        maxWidth: 500, 
        mx: 'auto',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Analytics sx={{ fontSize: '4rem', color: 'grey.400', mb: 3 }} />
        <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700, mb: 2 }}>
          No Data Available
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary', mb: 4 }}>
          Unable to load dashboard statistics at this time.
        </Typography>
        <Button 
          variant="contained" 
          onClick={onRetry} 
          startIcon={<Refresh sx={{ fontSize: '1.5rem' }} />}
          sx={{ 
            fontSize: '1.2rem',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 3
          }}
        >
          Retry
        </Button>
      </CardContent>
    </Card>
  </Box>
);

export const LinenDashboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [linenItemStats, setLinenItemStats] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load main dashboard stats
      const [dashboardRes, lowStockRes, itemStatsRes] = await Promise.all([
        getDashboardStats(),
        getLowStockItems(),
        getLinenItemStats()
      ]);
      
      setStats(dashboardRes.data?.stats || dashboardRes.data);
      setLowStockItems(lowStockRes.data?.items || []);
      setLinenItemStats(itemStatsRes.data || {});
      
      enqueueSnackbar("🎉 Dashboard updated successfully", { variant: "success" });
    } catch (err) {
      console.error("Dashboard load error:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to load dashboard";
      setError(errorMessage);
      enqueueSnackbar(`❌ ${errorMessage}`, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Initial load and retry mechanism
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData, retryCount]);

  // Auto-refresh data every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) {
        loadDashboardData();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [loadDashboardData, loading]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const handleManualRefresh = () => {
    loadDashboardData();
  };

  // Memoized data for performance
  const recentDispatches = useMemo(() => 
    stats?.recent_dispatches || [], 
    [stats?.recent_dispatches]
  );

  const topWards = useMemo(() => 
    stats?.top_wards || [], 
    [stats?.top_wards]
  );

  // Calculate available quantity for stats
  const totalAvailableItems = useMemo(() => {
    if (!stats) return 0;
    const clean = stats.linen_total_clean || 0;
    const dirty = stats.linen_dirty_pending || 0;
    const damaged = stats.total_damaged || 0;
    return Math.max(0, clean - (dirty + damaged));
  }, [stats]);

  // Calculate stock health percentage
  const stockHealthPercentage = useMemo(() => {
    if (!linenItemStats?.total_items || linenItemStats.total_items === 0) return 100;
    const lowStockCount = lowStockItems.length;
    return Math.max(0, ((linenItemStats.total_items - lowStockCount) / linenItemStats.total_items) * 100);
  }, [linenItemStats, lowStockItems]);

  // Show loading state
  if (loading && !stats) {
    return <DashboardLoading />;
  }

  // Show error state
  if (error && !stats) {
    return <ErrorFallback error={error} onRetry={handleRetry} />;
  }

  // Show empty state
  if (!stats) {
    return <EmptyState onRetry={handleRetry} />;
  }

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Enhanced Header */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <DashboardIcon sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Linen Management Dashboard
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Real-time analytics and insights for linen operations
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title="🔄 Refresh Data">
                <IconButton 
                  onClick={handleManualRefresh}
                  disabled={loading}
                  sx={{ 
                    backgroundColor: 'white',
                    color: 'primary.main',
                    width: 60,
                    height: 60,
                    '&:hover': { 
                      backgroundColor: 'grey.100',
                      transform: 'rotate(180deg)'
                    },
                    transition: 'all 0.5s ease',
                    '&:disabled': { backgroundColor: 'grey.300' }
                  }}
                >
                  {loading ? 
                    <CircularProgress size={30} /> : 
                    <Refresh sx={{ fontSize: 30 }} />
                  }
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <LowStockAlert items={lowStockItems} loading={loading} />
        </Box>
      )}

      {/* Enhanced Main Stats Cards Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Stock Overview */}
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="📦 Available Clean Items"
            value={totalAvailableItems.toLocaleString()}
            subtitle={`${stats.linen_total_clean || 0} total - ${stats.linen_dirty_pending || 0} dirty`}
            loading={loading}
            trend={1}
            icon={<Inventory />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="📊 Stock Health"
            value={`${Math.round(stockHealthPercentage)}%`}
            subtitle={`${lowStockItems.length} items need attention`}
            loading={loading}
            trend={stockHealthPercentage > 80 ? 1 : stockHealthPercentage > 60 ? 0 : -1}
            icon={<Analytics />}
            color={stockHealthPercentage > 80 ? 'success' : stockHealthPercentage > 60 ? 'warning' : 'error'}
            sx={{
              background: stockHealthPercentage > 80 ? 
                'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)' :
                stockHealthPercentage > 60 ? 
                'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)' :
                'linear-gradient(135deg, #F44336 0%, #E57373 100%)',
              color: 'white'
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="🚚 Today's Dispatches"
            value={stats.bags_dispatched_today || 0}
            subtitle={`${stats.pending_receives || 0} pending receives`}
            loading={loading}
            trend={1}
            icon={<LocalShipping />}
            color="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="🏢 Active Vendors"
            value={linenItemStats?.active_vendors || 0}
            subtitle={`${linenItemStats?.total_vendors || 0} total vendors`}
            loading={loading}
            trend={1}
            icon={<Business />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Enhanced Additional Stats Row */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardHeader
          title={
            <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
              📈 Detailed Statistics
            </Typography>
          }
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        />
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2}>
              <StatsCard
                title="📦 Total Items"
                value={linenItemStats?.total_items || 0}
                loading={loading}
                icon={<Inventory />}
                variant="outlined"
                sx={{ background: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatsCard
                title="📑 Categories"
                value={linenItemStats?.total_categories || 0}
                loading={loading}
                icon={<Business />}
                variant="outlined"
                sx={{ background: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatsCard
                title="🟡 Damaged Items"
                value={stats.total_damaged || 0}
                loading={loading}
                icon={<Warning />}
                variant="outlined"
                sx={{ background: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatsCard
                title="📤 This Week Issues"
                value={stats.weekly_issues || 0}
                loading={loading}
                icon={<TrendingUp />}
                variant="outlined"
                sx={{ background: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatsCard
                title="📥 This Week Returns"
                value={stats.weekly_returns || 0}
                loading={loading}
                icon={<TrendingDown />}
                variant="outlined"
                sx={{ background: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatsCard
                title="💰 Stock Value"
                value={`₹${(linenItemStats?.total_stock_value || 0).toLocaleString()}`}
                loading={loading}
                icon={<Analytics />}
                variant="outlined"
                sx={{ background: 'white' }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Tables Grid */}
      <Grid container spacing={4}>
        {/* Top Wards Table */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <CardHeader
              title={
                <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LocalShipping sx={{ fontSize: '2rem' }} />
                  Top Wards By Linen Usage
                </Typography>
              }
              subheader="Last 7 Days Performance"
              sx={{ 
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50'
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                <Table aria-label="Top wards by linen usage">
                  <TableHead sx={{ backgroundColor: 'primary.main' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🏥 Ward
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📊 Items Used
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📈 Trend
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topWards.map((row, index) => (
                      <TableRow 
                        key={row.ward || index}
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            transform: 'translateY(-1px)',
                            transition: 'all 0.3s ease'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        hover
                      >
                        <TableCell component="th" scope="row">
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {row.ward || 'Unknown Ward'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                            {row.used?.toLocaleString() || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {row.trend > 0 ? 
                            <TrendingUp color="success" sx={{ fontSize: '2rem' }} /> :
                           row.trend < 0 ? 
                            <TrendingDown color="error" sx={{ fontSize: '2rem' }} /> : 
                            <Timeline color="disabled" sx={{ fontSize: '2rem' }} />
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                    {topWards.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                          <Inventory sx={{ fontSize: '3rem', color: 'grey.400', mb: 2 }} />
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                            No usage data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Dispatches Table */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <CardHeader
              title={
                <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Inventory sx={{ fontSize: '2rem' }} />
                  Recent Dispatches
                </Typography>
              }
              subheader="Latest dispatch activities"
              sx={{ 
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50'
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                <Table aria-label="Recent dispatches">
                  <TableHead sx={{ backgroundColor: 'primary.main' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🚚 Dispatch #
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🏢 Vendor
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📦 Items
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📊 Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentDispatches.map((dispatch) => (
                      <TableRow 
                        key={dispatch._id}
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            transform: 'translateY(-1px)',
                            transition: 'all 0.3s ease'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        hover
                      >
                        <TableCell component="th" scope="row">
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {dispatch.dispatchNumber || dispatch._id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {dispatch.vendorName || dispatch.vendor || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={(dispatch.items || []).length}
                            size="medium"
                            color="primary"
                            variant="filled"
                            sx={{ fontSize: '1rem', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={dispatch.status || 'Unknown'}
                            size="medium"
                            color={
                              dispatch.status === 'completed' ? 'success' :
                              dispatch.status === 'pending' ? 'warning' :
                              dispatch.status === 'dispatched' ? 'info' :
                              'default'
                            }
                            variant="filled"
                            sx={{ fontSize: '1rem', fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {recentDispatches.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <LocalShipping sx={{ fontSize: '3rem', color: 'grey.400', mb: 2 }} />
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                            No recent dispatches
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enhanced Last Updated Timestamp */}
      <Card sx={{ mt: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Refresh color="primary" />
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
              Last updated: {new Date().toLocaleString()}
              {loading && ' (🔄 Updating...)'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};